import { Stack, Typography, useTheme } from "@mui/material";
import {
  BarChartIcon,
  TicketIcon,
  UserGroupIcon,
  UserPlusIcon,
} from "components/icons";
import { trackAiHome } from "../../analytics";
import { MOCK_SUGGESTIONS } from "../data/mockHomeAiChat";
import type { ChatSuggestion, ChatSuggestionIcon } from "../types";

interface ChatSuggestionsProps {
  /** Receives the chip's prompt, asked verbatim. */
  onSelect: (prompt: string) => void;
}

const ICONS: Record<
  ChatSuggestionIcon,
  | typeof TicketIcon
  | typeof BarChartIcon
  | typeof UserGroupIcon
  | typeof UserPlusIcon
> = {
  ticket: TicketIcon,
  chart: BarChartIcon,
  customer: UserGroupIcon,
  user: UserPlusIcon,
};

export const ChatSuggestions = ({ onSelect }: ChatSuggestionsProps) => {
  const theme = useTheme();

  const iconColors: Record<ChatSuggestionIcon, string> = {
    ticket: theme.palette.info.main,
    chart: theme.palette.warning.main,
    customer: theme.palette.success.main,
    user: theme.palette.primary.main,
  };

  const handleSelect = (suggestion: ChatSuggestion, position: number) => {
    trackAiHome("ai_proactive_suggestion_clicked", {
      suggestion_text: suggestion.label,
      suggestion_type: suggestion.analyticsType,
      position,
    });
    onSelect(suggestion.prompt);
  };

  return (
    <Stack sx={{ gap: "12px" }}>
      <Stack direction="row" sx={{ gap: "12px", flexWrap: "wrap" }}>
        {MOCK_SUGGESTIONS.map((suggestion, position) => {
          const Icon = ICONS[suggestion.icon];

          return (
            <Stack
              key={suggestion.id}
              component="button"
              type="button"
              direction="row"
              onClick={() => handleSelect(suggestion, position)}
              sx={{
                alignItems: "center",
                gap: "8px",
                p: "4px 8px",
                borderRadius: "4px",
                border: `1px solid ${theme.palette.neutral["015"]}`,
                backgroundColor: theme.palette.neutral.main,
                cursor: "pointer",
                ":hover": {
                  backgroundColor: theme.palette.neutral["005"],
                  borderColor: theme.palette.neutral["015"],
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: "16px",
                  color: iconColors[suggestion.icon],
                }}
              />
              <Typography
                variant="caption-xs"
                sx={{ color: theme.palette.lightBg.high }}
              >
                {suggestion.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};
